import { fromMarkdown } from "mdast-util-from-markdown";
import { toMarkdown } from "mdast-util-to-markdown";
import { gfmAutolinkLiteral } from "micromark-extension-gfm-autolink-literal";
import { gfmStrikethrough } from "micromark-extension-gfm-strikethrough";
import { gfmTable } from "micromark-extension-gfm-table";
import { gfmTaskListItem } from "micromark-extension-gfm-task-list-item";
import { gfmAutolinkLiteralFromMarkdown, gfmAutolinkLiteralToMarkdown } from "mdast-util-gfm-autolink-literal";
import { gfmStrikethroughFromMarkdown, gfmStrikethroughToMarkdown } from "mdast-util-gfm-strikethrough";
import { gfmTableFromMarkdown, gfmTableToMarkdown } from "mdast-util-gfm-table";
import { gfmTaskListItemFromMarkdown, gfmTaskListItemToMarkdown } from "mdast-util-gfm-task-list-item";
import type { Nodes, Root } from "mdast";

const syntaxExtensions = [
  gfmAutolinkLiteral(),
  gfmStrikethrough({ singleTilde: false }),
  gfmTable(),
  gfmTaskListItem()
];

const fromMarkdownExtensions = [
  gfmAutolinkLiteralFromMarkdown(),
  gfmStrikethroughFromMarkdown(),
  gfmTableFromMarkdown(),
  gfmTaskListItemFromMarkdown()
];

const toMarkdownExtensions = [
  gfmAutolinkLiteralToMarkdown(),
  gfmStrikethroughToMarkdown(),
  gfmTableToMarkdown(),
  gfmTaskListItemToMarkdown()
];

export function parseMarkdownAst(source: string): Root {
  return fromMarkdown(source, {
    extensions: syntaxExtensions,
    mdastExtensions: fromMarkdownExtensions
  });
}

export function serializeMarkdownAst(root: Root): string {
  return toMarkdown(root, {
    extensions: toMarkdownExtensions,
    bullet: "-",
    bulletOther: "*",
    listItemIndent: "one"
  }).trim();
}

export type MarkdownProtectedRange = { start: number; end: number };

export function collectMarkdownCodeRanges(root: Root): MarkdownProtectedRange[] {
  const ranges: MarkdownProtectedRange[] = [];
  walkMarkdownAst(root, (node) => {
    if ((node.type !== "code" && node.type !== "inlineCode") || node.position?.start.offset === undefined || node.position.end.offset === undefined) return;
    ranges.push({ start: node.position.start.offset, end: node.position.end.offset });
  });
  return ranges.sort((a, b) => a.start - b.start || a.end - b.end);
}

export function walkMarkdownAst(node: Nodes, visit: (node: Nodes, parent?: Nodes) => void, parent?: Nodes): void {
  visit(node, parent);
  if (!("children" in node) || !Array.isArray(node.children)) return;
  for (const child of node.children as Nodes[]) walkMarkdownAst(child, visit, node);
}
